import { SHIFT_STATUS_LABELS, type ShiftSummary, shiftStatusSchema } from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ClipboardList, Play } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { OpenShiftDialog } from "../../../../features/shifts/open-shift-dialog.js";
import { useShiftPermissions, useShifts } from "../../../../features/shifts/queries.js";
import {
  NoStations,
  ShiftStationField,
  ShiftStatusBadge,
  ShiftsPage,
  shiftsSearchShape,
  useShiftStation,
} from "../../../../features/shifts/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { quickActionSearch, useOpenFromQuickAction } from "../../../../lib/hooks.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount, formatDate, formatDateTime } from "../../../../lib/format.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const searchSchema = z.object({
  ...shiftsSearchShape,
  status: shiftStatusSchema.optional().catch(undefined),
  from: date,
  to: date,
  ...quickActionSearch,
});

export const Route = createFileRoute("/_app/vardiya/vardiyalar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ShiftListPage,
});

/** Vardiya listesi: iş günü aralığı, istasyon ve durum süzgeciyle cursor sayfalama. */
function ShiftListPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useShiftPermissions();
  const [opening, setOpening] = useState(false);

  return (
    <ShiftsPage
      title={t("menu.shifts")}
      description={t("shifts.list.description")}
      breadcrumb={[{ label: t("menu.shifts") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <ShiftList companyId={companyId} opening={opening} setOpening={setOpening} />
        ) : (
          <Alert tone="info">{t("shifts.noPermission")}</Alert>
        )
      }
    </ShiftsPage>
  );
}

function ShiftList({
  companyId,
  opening,
  setOpening,
}: {
  companyId: string;
  opening: boolean;
  setOpening: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const permissions = useShiftPermissions();
  const search = Route.useSearch();
  const station = useShiftStation(companyId);
  useOpenFromQuickAction(
    search.yeni === "1",
    () => setOpening(true),
    permissions.canManage && !station.isLoading && station.stationId !== null,
  );
  const setFilters = (patch: Partial<z.infer<typeof searchSchema>>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters = {
    stationId: station.stationId ?? undefined,
    status: search.status,
    from: search.from,
    to: search.to,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useShifts(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<ShiftSummary>[]>(
    () => [
      {
        id: "shiftNo",
        header: t("shifts.fields.shiftNo"),
        hideable: false,
        exportValue: (row) => row.shiftNo,
        cell: (row) => (
          <Link
            to="/vardiya/vardiyalar/$id"
            params={{ id: row.id }}
            className="font-mono underline underline-offset-4"
          >
            {row.shiftNo}
          </Link>
        ),
      },
      {
        id: "name",
        header: t("shifts.fields.name"),
        exportValue: (row) => row.name,
        cell: (row) => row.name,
      },
      {
        id: "businessDate",
        header: t("shifts.fields.businessDate"),
        exportValue: (row) => row.businessDate,
        cell: (row) => formatDate(row.businessDate),
      },
      {
        id: "station",
        header: t("shifts.station"),
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => SHIFT_STATUS_LABELS[row.status],
        cell: (row) => <ShiftStatusBadge status={row.status} />,
      },
      {
        id: "openedAt",
        header: t("shifts.fields.openedAt"),
        exportValue: (row) => row.openedAt,
        cell: (row) => formatDateTime(row.openedAt),
      },
      {
        id: "closedAt",
        header: t("shifts.fields.closedAt"),
        // Vardiyanın BİTTİĞİ an kapanışın başladığı andır (endeksler o an okunur); `closedAt` hesaplar teslim
        // edildikten sonra "Vardiyayı kapat"a basılan idari andır — saatler, geçmişe dönük girişte günler sonra.
        exportValue: (row) => row.closingStartedAt ?? row.closedAt,
        cell: (row) => formatDateTime(row.closingStartedAt ?? row.closedAt),
      },
      {
        id: "memberCount",
        header: t("shifts.fields.memberCount"),
        align: "right",
        exportValue: (row) => String(row.memberCount),
        cell: (row) => formatCount(row.memberCount),
      },
      {
        id: "nozzleCount",
        header: t("shifts.fields.nozzleCount"),
        align: "right",
        exportValue: (row) => String(row.nozzleCount),
        cell: (row) => formatCount(row.nozzleCount),
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;

  return (
    <>
      {permissions.canManage ? (
        <div className="flex justify-end">
          <Button onClick={() => setOpening(true)}>
            <Play aria-hidden="true" />
            {t("shifts.open.title")}
          </Button>
        </div>
      ) : null}
      <DataTable
        caption={t("shifts.list.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ClipboardList />}
            title={t("shifts.list.emptyTitle")}
            description={t("shifts.list.emptyDescription")}
          />
        }
        filters={
          <>
            <ShiftStationField selection={station} />
            <FormField label={t("common.status")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) =>
                    setFilters({
                      status:
                        event.target.value === "" ? undefined : shiftStatusSchema.parse(event.target.value),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {shiftStatusSchema.options.map((status) => (
                    <option key={status} value={status}>
                      {SHIFT_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("shifts.list.from")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.from ?? null}
                  onValueChange={(value) => setFilters({ from: value ?? undefined })}
                />
              )}
            </FormField>
            <FormField label={t("shifts.list.to")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.to ?? null}
                  onValueChange={(value) => setFilters({ to: value ?? undefined })}
                />
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
      />
      <OpenShiftDialog
        open={opening}
        onOpenChange={setOpening}
        companyId={companyId}
        stations={station.stations}
        defaultStationId={station.stationId}
      />
    </>
  );
}
